import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './ui/button';
import { Link, useLocation } from 'react-router-dom';
import { Crown, BookOpen, Gift, CalendarClock, Menu, X, HelpCircle, FileText, Tag, MessageSquareQuote, Send, Scissors, PhoneCall } from 'lucide-react';
import LanguageSwitcher from './LanguageSwitcher';

const LOGO = 'https://customer-assets.emergentagent.com/job_87cd0f70-4f18-4348-a2fd-e2666a61734d/artifacts/ozrawt0t_IMG_0439.jpeg';

// Single source of truth for all header menu links — keeps desktop & mobile in sync.
const MENU_LINKS = [
  { key: 'pricing', label: 'Pricing', href: '#pricing', type: 'anchor', icon: Tag },
  { key: 'reviews', label: 'Reviews', href: '#videos', type: 'anchor', icon: MessageSquareQuote },
  { key: 'giveaways', label: 'Giveaways', to: '/giveaways', type: 'route', icon: Gift, accent: true, testid: 'nav-giveaways-link' },
  { key: 'clip', label: 'Earn (Clip)', to: '/clip', type: 'route', icon: Scissors, accent: true, testid: 'nav-clip-link' },
  { key: 'book-call', label: 'Book Call', to: '/book-call', type: 'route', icon: CalendarClock, accent: true, testid: 'nav-book-call-link' },
  { key: 'personal-call', label: 'Personal Call', to: '/personal-call', type: 'route', icon: PhoneCall, accent: true, testid: 'nav-personal-call-link' },
  { key: 'blog', label: 'Blog', to: '/blog', type: 'route', icon: BookOpen },
  { key: 'faq', label: 'FAQ', href: '#faq', type: 'anchor', icon: HelpCircle },
  { key: 'disclaimer', label: 'Disclaimer', to: '/disclaimer', type: 'route', icon: FileText },
  { key: 'elite', label: 'Elite', href: '#pricing', type: 'anchor', icon: Crown, accent: true },
];

function MenuItem({ link, onNavigate }) {
  const Icon = link.icon;
  const baseCls = 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12.5px] font-medium whitespace-nowrap transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(43_92%_56%)]/40';
  const colorCls = link.accent
    ? 'text-[hsl(43_92%_72%)] hover:text-[hsl(43_92%_60%)] hover:bg-[rgba(245,185,66,0.10)]'
    : 'text-[hsl(0_0%_82%)] hover:text-white hover:bg-white/[0.06]';
  const cls = `${baseCls} ${colorCls}`;

  if (link.type === 'route') {
    return (
      <Link to={link.to} className={cls} data-testid={link.testid || `nav-${link.key}-link`} onClick={onNavigate}>
        <Icon className="h-3.5 w-3.5" />
        <span>{link.label}</span>
      </Link>
    );
  }
  return (
    <a href={link.href} className={cls} data-testid={`nav-${link.key}-link`} onClick={onNavigate}>
      <Icon className="h-3.5 w-3.5" />
      <span>{link.label}</span>
    </a>
  );
}

export default function Navbar({ settings }) {
  const tg = settings?.telegram_link || '#';
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Close mobile menu on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [mobileOpen]);

  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-[hsla(0,0%,4%,0.72)] border-b border-white/[0.06]">
      <div className="container-x flex items-center justify-between h-16 gap-3">
        {/* LEFT — Logo + Brand */}
        <Link to="/" className="flex items-center gap-2 shrink-0 notranslate" translate="no" data-testid="nav-logo-link">
          <img src={LOGO} alt="AlveeFx" className="h-9 w-9 rounded-lg gold-hairline" />
          <span className="font-display uppercase text-xl tracking-wide" data-testid="nav-brand-text">AlveeFx</span>
        </Link>

        {/* CENTER — Glass Pill with all menu links (desktop only) */}
        <nav
          aria-label="Primary"
          data-testid="nav-glass-pill"
          className="hidden lg:flex items-center gap-1 px-2 py-1.5 rounded-full bg-white/[0.04] backdrop-blur-xl border border-white/[0.08] shadow-[0_8px_30px_-12px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.06)]"
        >
          {MENU_LINKS.map((link) => (
            <MenuItem key={link.key} link={link} />
          ))}
        </nav>

        {/* RIGHT — Language + Join CTA + Mobile Toggle */}
        <div className="flex items-center gap-2 shrink-0">
          <LanguageSwitcher />
          <a href={tg} target="_blank" rel="noreferrer" data-testid="nav-join-telegram-button" className="inline-flex">
            <Button className="cta-sheen border-0 text-white font-semibold px-3 sm:px-4 h-9 text-xs sm:text-sm gap-1.5">
              <Send className="h-3.5 w-3.5" />
              <span>Join Free</span>
            </Button>
          </a>
          {/* Mobile menu toggle */}
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            data-testid="nav-mobile-toggle"
            className="lg:hidden inline-flex items-center justify-center h-9 w-9 rounded-full border border-white/[0.08] bg-white/[0.04] backdrop-blur-md text-white hover:bg-white/[0.08] transition-colors"
          >
            {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* Mobile / Tablet Drawer — portaled to body to escape backdrop-filter containing block */}
      {mobileOpen && typeof document !== 'undefined' && createPortal(
        <div
          className="lg:hidden fixed top-16 right-0 bottom-0 left-0 z-50 bg-[hsl(0_0%_4%)] animate-in fade-in duration-200 overflow-y-auto"
          data-testid="nav-mobile-drawer"
          onClick={() => setMobileOpen(false)}
        >
          <div
            className="container-x pt-6 pb-10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col gap-1.5 p-3 rounded-2xl bg-[hsl(240_6%_8%)] border border-white/[0.08] shadow-2xl">
              {MENU_LINKS.map((link) => {
                const Icon = link.icon;
                const colorCls = link.accent
                  ? 'text-[hsl(43_92%_72%)] hover:bg-[rgba(245,185,66,0.12)] active:bg-[rgba(245,185,66,0.18)]'
                  : 'text-[hsl(0_0%_92%)] hover:bg-white/[0.06] active:bg-white/[0.10]';
                const cls = `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${colorCls}`;
                if (link.type === 'route') {
                  return (
                    <Link key={link.key} to={link.to} className={cls} data-testid={`nav-mobile-${link.key}-link`} onClick={() => setMobileOpen(false)}>
                      <Icon className="h-4 w-4" />
                      <span>{link.label}</span>
                    </Link>
                  );
                }
                return (
                  <a key={link.key} href={link.href} className={cls} data-testid={`nav-mobile-${link.key}-link`} onClick={() => setMobileOpen(false)}>
                    <Icon className="h-4 w-4" />
                    <span>{link.label}</span>
                  </a>
                );
              })}
              <a
                href={tg}
                target="_blank"
                rel="noreferrer"
                onClick={() => setMobileOpen(false)}
                data-testid="nav-mobile-join-telegram-button"
                className="mt-2"
              >
                <Button className="cta-sheen border-0 text-white font-semibold w-full h-11">Join Free Telegram</Button>
              </a>
            </div>
          </div>
        </div>,
        document.body
      )}
    </header>
  );
}
